import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { BadgeCheck, Shield, Quote, MessageSquareQuote } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const QuotedPost = ({ quotedPost, onClick }) => {
  const navigate = useNavigate();

  if (!quotedPost) return null;

  const goToQuotedPost = (e) => {
    e.stopPropagation();
    if (onClick) {
      onClick(quotedPost.post_id);
    } else {
      navigate(`/post/${quotedPost.post_id}`);
    }
  };

  return (
    <div 
      onClick={goToQuotedPost}
      className="mt-3 p-3 rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
      data-testid="quoted-post"
    >
      {/* Quoted Post Header */}
      <div className="flex items-center gap-2 mb-2">
        <Avatar className="w-5 h-5">
          <AvatarImage src={quotedPost.user?.avatar} />
          <AvatarFallback className="bg-primary text-white text-xs">
            {quotedPost.user?.name?.charAt(0)}
          </AvatarFallback>
        </Avatar>
        <div className="flex items-center gap-1 min-w-0">
          <span className="font-semibold text-sm text-zinc-800 dark:text-zinc-200 truncate">
            {quotedPost.user?.name}
          </span>
          {quotedPost.user?.is_verified && (
            <BadgeCheck className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
          )}
          <span className="text-zinc-500 text-xs truncate">
            @{quotedPost.user?.username}
          </span>
        </div>
        {quotedPost.created_at && (
          <span className="text-xs text-zinc-400 ml-auto flex-shrink-0">
            {formatDistanceToNow(new Date(quotedPost.created_at), { addSuffix: true })}
          </span>
        )}
      </div>

      {/* Quoted Post Content */}
      {quotedPost.content && (
        <p className="text-sm text-zinc-700 dark:text-zinc-300 line-clamp-3 mb-2">
          {quotedPost.content}
        </p>
      )}

      {/* Quoted Post Media */}
      {quotedPost.images && quotedPost.images.length > 0 && (
        <div className="mt-2">
          <img 
            src={quotedPost.images[0]} 
            alt="" 
            className="max-h-32 rounded-lg object-cover"
          />
        </div>
      )}

      {quotedPost.gif_url && (
        <img 
          src={quotedPost.gif_url} 
          alt="GIF" 
          className="max-h-32 rounded-lg mt-2"
        />
      )}
    </div>
  );
};

export default QuotedPost;

// Quote button component
export const QuoteButton = ({ onClick, quotesCount = 0 }) => {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1 px-2 py-1 text-zinc-500 hover:text-purple-500 transition-colors rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20"
      data-testid="quote-btn"
      title="Quote this post"
    >
      <MessageSquareQuote className="w-4 h-4" />
      {quotesCount > 0 && <span className="text-sm font-medium">{quotesCount}</span>}
    </button>
  );
};
